/**
 * Transactions API Service
 * Handles all transaction-related API calls
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    TransactionListResponse,
    TransactionListApiResponse,
    TransactionDetailResponse,
    TransactionFilters,
} from '@/types/transaction'

/**
 * Get Transactions List
 * GET /admin/transactions
 * @param filters - Optional query parameters for filtering transactions
 */
export const getTransactions = async (
    filters?: TransactionFilters
): Promise<TransactionListResponse> => {
    const response = await axiosInstance.get<TransactionListApiResponse>(
        API_ROUTES.TRANSACTIONS.LIST,
        {
            params: filters,
        }
    )
    return response.data.data
}

/**
 * Get Transaction by ID
 * GET /api/v1/admin/transactions/{id}
 * @param id - Transaction ID
 */
export const getTransactionById = async (
    id: string
): Promise<TransactionDetailResponse> => {
    // The API wraps every result as { status, statusCode, message, data }.
    const response = await axiosInstance.get<{
        data: TransactionDetailResponse
    }>(API_ROUTES.TRANSACTIONS.DETAIL(id))
    return response.data.data
}

/**
 * Helper: trigger a browser download from a blob response
 */
const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
}

/**
 * Download receipt PDF for a single transaction
 * GET /admin/transactions/download/receipt/{id}
 */
export const downloadTransactionReceipt = async (id: string): Promise<void> => {
    const response = await axiosInstance.get(
        API_ROUTES.TRANSACTIONS.DOWNLOAD_RECEIPT(id),
        { responseType: 'blob' }
    )
    triggerBlobDownload(response.data as Blob, `receipt-${id}.pdf`)
}

/**
 * Download receipt PDFs for multiple transactions
 * POST /admin/transactions/download/receipts
 */
export const downloadMultipleTransactionReceipts = async (
    transactionIds: string[]
): Promise<void> => {
    const response = await axiosInstance.post(
        API_ROUTES.TRANSACTIONS.DOWNLOAD_RECEIPTS,
        { transactionIds },
        { responseType: 'blob' }
    )
    triggerBlobDownload(
        response.data as Blob,
        `receipts-${transactionIds.length}-transactions.pdf`
    )
}

export interface DownloadUserHistoryParams {
    userId: string
    startDate?: string
    endDate?: string
    status?: string
    type?: string
    currency?: string
}

/**
 * Download transaction history PDF for a single user
 * POST /admin/transactions/download/user-history
 */
export const downloadUserTransactionHistory = async (
    params: DownloadUserHistoryParams
): Promise<void> => {
    const response = await axiosInstance.post(
        API_ROUTES.TRANSACTIONS.DOWNLOAD_USER_HISTORY,
        params,
        { responseType: 'blob' }
    )
    triggerBlobDownload(
        response.data as Blob,
        `transaction-history-${params.userId}.pdf`
    )
}

export interface DownloadUsersHistoryParams {
    userIds: string[]
    startDate?: string
    endDate?: string
    status?: string
    type?: string
    currency?: string
}

/**
 * Download transaction history PDF for multiple users
 * POST /admin/transactions/download/users-history
 */
export const downloadUsersTransactionHistory = async (
    params: DownloadUsersHistoryParams
): Promise<void> => {
    const response = await axiosInstance.post(
        API_ROUTES.TRANSACTIONS.DOWNLOAD_USERS_HISTORY,
        params,
        { responseType: 'blob' }
    )
    triggerBlobDownload(
        response.data as Blob,
        `transaction-history-${params.userIds.length}-users.pdf`
    )
}
